'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ImportExportOutlined } from '@mui/icons-material';
import { ButtonBase, Divider, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { menuItemClasses } from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@prismo-io/design-system';
import { Avatar, CascadingMenuItem, CascadingMenus } from '@prismo-io/design-system';
import { SignOutModal } from '@prismo-io/feature-auth/components/sign-out-modal';
import type { AuthMeSchemaT } from '@prismo-io/feature-auth/utils';
import { Application, translate } from '@prismo-io/router';
import { LanguageCode, type LanguageCodeT, type ToolsOrganizationsT, type UserAccessTypeT } from '@prismo-io/schemas';
import { type BaseSyntheticEvent, type FC, useState } from 'react';
import { PdfDownloadsMenu } from '../pdf-downloads-menu';
type Props = {
  user: AuthMeSchemaT;
  access: UserAccessTypeT;
  organization?: Pick<ToolsOrganizationsT, 'id' | 'email'>;
  hasInvitation?: boolean;
  isAdminTooler?: boolean;
  withCV?: boolean;
};
const buildToolsUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.TOOLS,
    path: `/`,
    addHostname: true,
    locale
  });
};
const buildProfileUrl = (locale: LanguageCodeT, userUrl: string) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.APP,
    path: `/profil/:user`,
    params: {
      user: userUrl
    },
    locale
  });
};
const buildParametersUrl = (locale: LanguageCodeT, userUrl: string) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.APP,
    path: `/profil/:user/parametres`,
    params: {
      user: userUrl
    },
    locale
  });
};
const buildInvitationsUrl = (locale: LanguageCodeT, userUrl: string) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.APP,
    path: `/profil/:user/invitations`,
    params: {
      user: userUrl
    },
    locale
  });
};
const buildFAQUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.TOOLS,
    path: `/`,
    addHostname: true,
    locale
  });
};
const buildContactUsUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.TOOLS,
    path: `/`,
    addHostname: true,
    locale
  });
};
export const UserPreferencesMenu: FC<Props> = (props: Props) => {
  const {
    user,
    access,
    organization = undefined,
    hasInvitation = false,
    isAdminTooler = false,
    withCV = false
  } = props;
  const {
    i18n
  } = useLingui();
  const {
    locale = LanguageCode.Enum.fr
  } = i18n;
  const toolsUrl = buildToolsUrl(locale as LanguageCodeT);
  const profileUrl = buildProfileUrl(locale as LanguageCodeT, user.url);
  const parametersUrl = buildParametersUrl(locale as LanguageCodeT, user.url);
  const invitationsUrl = buildInvitationsUrl(locale as LanguageCodeT, user.url);
  const FAQUrl = buildFAQUrl(locale as LanguageCodeT);
  const contactUsUrl = buildContactUsUrl(locale as LanguageCodeT);
  const [anchorEl, setAnchorEl] = useState(null);
  const [signOutDialog, setSignOutDialog] = useState<boolean>(false);
  function handleClick(event: BaseSyntheticEvent) {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  }
  function handleClose() {
    setAnchorEl(null);
  }
  return <>
      <CascadingMenus variant="popover" popupId="menu-preferences" action="click" button={props => <ButtonBase {...props} data-testid="me-preferences-menu">
            <Tooltip title={user.full_name} placement="left">
              <Avatar avatar={{
          sx: {
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            fontSize: 16,
            fontWeight: 'bold'
          }
        }} fill src={user.avatar} alt={t`Image de profil de ${user.full_name}`} replaceOnError={user.initials} />
            </Tooltip>
          </ButtonBase>} data-sentry-element="CascadingMenus" data-sentry-source-file="index.tsx">
        {isAdminTooler && <>
            <CascadingMenuItem href={toolsUrl} target="_blank" sx={{
          paddingY: 2
        }}>
              <ListItemIcon sx={{
            minWidth: 'auto !important'
          }}>
                <ImportExportOutlined color="gray" fontSize="small" sx={{
              transform: 'rotate(90deg)'
            }} />
              </ListItemIcon>
              <ListItemText>
                <Trans>Accès à PRISMO Tools</Trans>
              </ListItemText>
            </CascadingMenuItem>

            <Divider sx={{
          borderColor: '#f0f0f0',
          marginBottom: '0.5rem'
        }} />
          </>}

        <CascadingMenuItem href={profileUrl} sx={{
        paddingY: 2
      }} data-sentry-element="CascadingMenuItem" data-sentry-source-file="index.tsx">
          <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Mon profil PRISMO App</Trans>
        </CascadingMenuItem>
        <CascadingMenuItem href={parametersUrl} sx={{
        paddingY: 2
      }} data-testid="settings-menu-item" data-sentry-element="CascadingMenuItem" data-sentry-source-file="index.tsx">
          <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Mes paramètres</Trans>
        </CascadingMenuItem>
        {hasInvitation && <CascadingMenuItem href={invitationsUrl} sx={{
        paddingY: 2
      }}>
            <Trans>Mes invitations</Trans>
          </CascadingMenuItem>}
        <PdfDownloadsMenu user={user} access={access} organization={organization} menuTitle={t`Mes rapports`} anchorOrigin={{
        horizontal: 'left',
        vertical: 'top'
      }} transformOrigin={{
        horizontal: 'right',
        vertical: 'top'
      }} cvIsEnable={withCV} data-sentry-element="PdfDownloadsMenu" data-sentry-source-file="index.tsx" />

        <Divider sx={{
        borderColor: '#f0f0f0',
        marginBottom: '0.5rem'
      }} data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
        <CascadingMenuItem href="https://prismo.io/faq-prismo/" target="_blank" sx={{
        paddingY: 2
      }} data-sentry-element="CascadingMenuItem" data-sentry-source-file="index.tsx">
          <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">FAQ & Politique de confidentialité</Trans>
        </CascadingMenuItem>
        <CascadingMenuItem href="https://prismo.io/contactez-nous/" target="_blank" data-sentry-element="CascadingMenuItem" data-sentry-source-file="index.tsx">
          <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Nous contacter</Trans>
        </CascadingMenuItem>
        <Divider sx={{
        borderColor: '#f0f0f0'
      }} data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
        <CascadingMenuItem onClick={() => setSignOutDialog(true)} data-testid="logout-menu-item" sx={(theme: Theme) => ({
        paddingY: 2,
        [`&.${menuItemClasses.root}`]: {
          color: theme.palette.error.main,
          '&:hover': {
            backgroundColor: alpha(theme.palette.error.light, 0.2)
          }
        }
      })} data-sentry-element="CascadingMenuItem" data-sentry-source-file="index.tsx">
          <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Déconnexion</Trans>
        </CascadingMenuItem>
      </CascadingMenus>
      <SignOutModal modalIsOpen={signOutDialog} onCloseDialog={() => setSignOutDialog(false)} data-sentry-element="SignOutModal" data-sentry-source-file="index.tsx" />

      {/* <Button
        variant="text"
        sx={{ padding: 0 }}
        aria-owns={anchorEl ? 'user-preferences-menu' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
       >
        <Tooltip title={user.full_name} placement="left">
          <Avatar
            avatar={{
              sx: {
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: 16,
                fontWeight: 'bold',
              },
            }}
            fill
            src={user.avatar}
            alt={t`Image de profil de ${user.full_name}`}
            replaceOnError={user.initials}
          />
        </Tooltip>
       </Button> */}
      {/* <Menu
        id="user-preferences-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        // MenuListProps={{ onMouseLeave: handleClose }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{ marginTop: '0.5rem' }}
       >
        {user.has_tools && (
          <>
            <MenuItem onClick={handleClose}>
              <ListItemButton
                disableGutters
                LinkComponent={Link}
                href={toolsUrl}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  backgroundColor: 'transparent !important',
                }}
                // sx={{ backgroundColor: 'transparent !important' }}
              >
                <ListItemIcon sx={{ minWidth: 'auto !important' }}>
                  <AiOutlineSwap fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Trans>Accès à PRISMO Tools</Trans>
                </ListItemText>
              </ListItemButton>
            </MenuItem>
            <Divider sx={{ borderColor: '#f0f0f0' }} />
          </>
        )}
        <MenuItem onClick={handleClose}>
          <ListItemButton
            disableGutters
            LinkComponent={Link}
            href={profileUrl}
            sx={{ backgroundColor: 'transparent !important' }}
          >
            <ListItemText>
              <Trans>Mon profil PRISMO App</Trans>
            </ListItemText>
          </ListItemButton>
        </MenuItem>
        <MenuItem onClick={handleClose}>
          <ListItemButton
            disableGutters
            LinkComponent={Link}
            href={parametersUrl}
            sx={{ backgroundColor: 'transparent !important' }}
          >
            <ListItemText>
              <Trans>Mes paramètres</Trans>
            </ListItemText>
          </ListItemButton>
        </MenuItem>
        {hasInvitation && (
          <MenuItem onClick={handleClose}>
            <ListItemButton
              disableGutters
              LinkComponent={Link}
              href={invitationsUrl}
              sx={{ backgroundColor: 'transparent !important' }}
            >
              <ListItemText>
                <Trans>Mes invitations</Trans>
              </ListItemText>
            </ListItemButton>
          </MenuItem>
        )}
        <PdfDownloadsMenu
          user={user}
          access={access}
          organizationId={organizationId}
          menuTitle={t`Mes rapports`}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        />
        <Divider sx={{ borderColor: '#f0f0f0', marginBottom: '0.5rem' }} />
        <MenuItem onClick={handleClose}>
          <ListItemButton
            disableGutters
            href={'https://prismo.io/faq-prismo/'}
            target="_blank"
            sx={{ backgroundColor: 'transparent !important' }}
          >
            <ListItemText>
              <Trans>FAQ & Politique de confidentialité</Trans>
            </ListItemText>
          </ListItemButton>
        </MenuItem>
        <MenuItem onClick={handleClose}>
          <ListItemButton
            disableGutters
            href={'https://prismo.io/contactez-nous/'}
            target="_blank"
            sx={{ backgroundColor: 'transparent !important' }}
          >
            <ListItemText>
              <Trans>Nous contacter</Trans>
            </ListItemText>
          </ListItemButton>
        </MenuItem>
        <Divider sx={{ borderColor: '#f0f0f0' }} />
        <MenuItem>
          <SignOutModal />
          <ListItemButton disableGutters>
            <ListItemText>
              <Trans>Déconnexion</Trans>
            </ListItemText>
          </ListItemButton>
        </MenuItem>
       </Menu> */}
    </>;
};